import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { useDocumentContext } from '../../../context/complexDocumentContext';
import { VARIABLE_TYPE } from '../../../utils/constants/appConstants';
// import { getDocumentDetails } from '../complexDocumentHelper';
import { getDocumentDetails } from '../accessibilityHelper';
import { generateAccessibilityScript } from '../../../utils/services/complexDocumentsAPI';
import { Alert } from 'antd';
import { StyledAccessibilityPreview } from './styles';
import { withTranslation } from 'react-i18next';
import { ACCESSIBILITY_PREVIEW } from './translationValues';
import blankCanvas from '../../../images/blank-canvas.png'

const NAMESPACE = ['ACCESSIBILITY_PREVIEW'];
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

const AccessibilityPreview = ({ t, PreviewLoader, documentVariables, examplValuesData, isEmail = false, emailSubject }) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [voiceSpeed, setVoiceSpeed] = useState(1);
  const [voices, setVoices] = useState([]);
  const [voiceListVisible, setVoiceListVisible] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [words, setWords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getCurrentDesign = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(getCurrentDesign);

  const contextSelector = useCallback(store => store, []);
  const [documentContext] = useDocumentContext(contextSelector);

  const lang = documentContext?.language || 'en';

  const utteranceRef = useRef(null);
  const containerRef = useRef(null);
  const initialPlayRef = useRef(null);

  const docDesign = useRef(null);
  const pageandUrl = useRef(null);
  const isDocumentSummary = useRef(null);

  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      const filteredVoices = voices.filter(voice => voice.lang.startsWith(lang) && voice.localService);
      setVoices(filteredVoices);
      setSelectedVoice(filteredVoices.find(voice => voice?.default) || filteredVoices[0]);
    };
    loadVoices();
    const handleBeforeUnload = () => {
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
      }
    };
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    getDocumentDetails({
      documentContext,
      currentDesign,
      visible: true,
      isDocumentSummary,
      pageandUrl,
      docDesign,
      isEmail,
      emailSubject,
    });
    const getScript = async () => {
      setIsLoading(true);
      const documentDTO = docDesign.current;
      documentDTO.variableContract = documentVariables.map(variable => ({
        type: variable.type === VARIABLE_TYPE.TABLE_OBJECT ? VARIABLE_TYPE.OBJECT : variable.type,
        variableId: variable.id,
        name: variable.id ? variable.placeHolderText : variable.name,
        content: variable.variables?.map(childVariable => ({
          type: childVariable.type,
          variableId: childVariable.id,
          name: childVariable.id ? childVariable.placeHolderText : childVariable.name,
        })),
      }));

      let dataSet = {};
      documentVariables.forEach(variable => {
        dataSet = {
          ...dataSet,
          uniq_id: '',
          uniq_name: '',
        };
        if (variable.type === VARIABLE_TYPE.OBJECT || variable.type === VARIABLE_TYPE.TABLE_OBJECT) {
          let listArray = [];
          let d = examplValuesData[variable.name]?.value?.forEach(eachBlock => {
            let blockSamples = {};
            eachBlock.value.forEach(block => {
              if (block.type === VARIABLE_TYPE.CHART_OBJECT) {
                blockSamples[block.name] = block.value.source;
              } else {
                blockSamples[block.name] = block.value;
              }
            });
            listArray.push(blockSamples);
          });
          dataSet = {
            ...dataSet,
            [variable.name]: listArray,
          };
        } else if (
          variable.type === VARIABLE_TYPE.CHART_OBJECT ||
          variable.type === VARIABLE_TYPE.FORM_OBJECT
        ) {
          const listArray = [];
          examplValuesData[variable.name] &&
            examplValuesData[variable.name].value &&
            examplValuesData[variable.name].value.forEach(eachBlock => {
              let blockSamples = {};
              eachBlock.value.forEach(block => {
                blockSamples[block.name] = block.value.source;
              });
              listArray.push(blockSamples);
            });
          dataSet = {
            ...dataSet,
            ...listArray[0],
          };
        } else {
          dataSet = {
            ...dataSet,
            [variable.placeHolderText]: examplValuesData[variable.placeHolderText]?.value || '',
          };
        }
      });
      try {
        const response = await generateAccessibilityScript({ dataSet, documentDTO });
        if (response && response?.statusCode === 1940) {
          if (response.data) {
            const script = response.data
              .split('\n') // split by newline
              .map(line => line.trim()) // trim each line
              .filter(line => line.length > 0) // remove empty lines
              .flatMap((line, index) => {
                const words = line.split(/\s+/); // split by whitespace
                words[0] = (index === 0 ? '' : '\n') + words[0]; // prefix first word in line with \n except first line
                return words;
              });
            setWords(script);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    getScript();
  }, []);

  useEffect(() => {
    if (currentWordIndex !== null) {
      const currentWord = document.getElementById(`word-${currentWordIndex}`);
      const containerRect = containerRef.current.getBoundingClientRect();
      const wordRect = currentWord.getBoundingClientRect();
      const diff = wordRect.top - containerRect.top;
      if (diff > 150) {
        containerRef.current.scrollTop = containerRef.current.scrollTop + 50;
      }
    }
  }, [currentWordIndex]);

  const speak = (script = '', id = 0, rate = 1, voice = null) => {
    const utterance = new SpeechSynthesisUtterance(script);
    utterance.lang = selectedVoice.lang;
    utterance.rate = rate;
    if (voice) {
      utterance.voice = voice;
    }
    utterance.onboundary = event => {
      if (event.name === 'word') {
        const charIndex = event.charIndex;
        let index = id;
        let totalChars = 0;
        for (let i = id; i < words.length; i++) {
          totalChars += words[i].length + 1; // +1 for space
          if (charIndex < totalChars) {
            index = i;
            break;
          }
        }
        setCurrentWordIndex(index);
      }
    };
    utterance.onend = () => {
      setIsPlaying(false);
      setCurrentWordIndex(null);
    };
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setIsPlaying(true);
  };

  const stop = () => {
    setIsPlaying(false);
    setCurrentWordIndex(null);
    window.speechSynthesis.cancel();
    initialPlayRef.current = false;
  };

  const pause = () => {
    setIsPlaying(false);
    window.speechSynthesis.pause();
  };

  const readFromIndex = (index, rate, voice) => {
    const wordIndex = index || 0;
    stop();
    setCurrentWordIndex(wordIndex);
    const remainingText = words.slice(wordIndex).join(' ');
    if (voice) {
      speak(remainingText, wordIndex, rate, voice);
    } else {
      speak(remainingText, wordIndex, rate);
    }
  };

  const disable = !window.speechSynthesis || !voices?.length;

  return (
    <StyledAccessibilityPreview isRTLLanguage={RTL_LANGUAGES.includes(lang)}>
      {isLoading ? (
        <PreviewLoader />
      ) : (
        <>
          <div className={`container ${!words.length ? 'hide-background' : ''}`}>
            <div className="script scroll-container" ref={containerRef}>
              {words?.length > 0 ? (
                words.map((word, id) => (
                  <>
                    {word.startsWith('\n') && <br />}
                    <span
                      key={id}
                      id={`word-${id}`}
                      className={
                        !disable && `script-word ${id === currentWordIndex ? 'current-word' : ''}`
                      }
                      onClick={() => !disable && readFromIndex(id, voiceSpeed, selectedVoice)}
                    >
                      {word}{' '}
                    </span>
                  </>
                ))
              ) : (
                <div className="empty">
                  <img src={blankCanvas} alt="blank-canvas" />
                  <p>
                    {t(
                      ACCESSIBILITY_PREVIEW.CONTENT_NOT_AVAILABLE.key,
                      ACCESSIBILITY_PREVIEW.CONTENT_NOT_AVAILABLE.value,
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>

          {words?.length > 0 && (
            <div className="controls">
              <div className="control-key">
                <button onClick={stop} disabled={disable || !isPlaying}>
                  <i className="fa-solid fa-stop"></i>
                  <span className={'label'}>
                    {t(
                      ACCESSIBILITY_PREVIEW.STOP_LABEL.key,
                      ACCESSIBILITY_PREVIEW.STOP_LABEL.value,
                    )}
                  </span>
                </button>
              </div>
              <div className="control-key">
                <button
                  className={`play-button ${isPlaying ? 'playing' : ''}`}
                  onClick={() => {
                    if (isPlaying) {
                      pause();
                    } else {
                      containerRef.current.scrollTop = 0;
                      if (initialPlayRef.current) {
                        readFromIndex(currentWordIndex, voiceSpeed, selectedVoice);
                      } else {
                        readFromIndex(0, voiceSpeed, selectedVoice);
                      }
                    }
                    setIsPlaying(!isPlaying);
                    initialPlayRef.current = true;
                  }}
                  disabled={disable}
                >
                  {isPlaying ? (
                    <i className="fa-solid fa-pause"></i>
                  ) : (
                    <i className="fa-solid fa-play"></i>
                  )}
                </button>
              </div>
              <div className={`control-key ${disable ? 'disabled-text' : ''}`}>
                <div className="speed-control">
                  <button
                    onClick={() => {
                      if (voiceSpeed > 0.5) {
                        setVoiceSpeed(rate => rate - 0.5);
                        if (isPlaying)
                          readFromIndex(currentWordIndex, voiceSpeed - 0.5, selectedVoice);
                      }
                    }}
                    disabled={disable || voiceSpeed <= 0.5}
                  >
                    <i className="fa-solid fa-caret-left"></i>
                  </button>
                  <p>{voiceSpeed}x</p>
                  <button
                    onClick={() => {
                      if (voiceSpeed < 5) {
                        setVoiceSpeed(rate => rate + 0.5);
                        if (isPlaying)
                          readFromIndex(currentWordIndex, voiceSpeed + 0.5, selectedVoice);
                      }
                    }}
                    disabled={disable || voiceSpeed >= 5}
                  >
                    <i class="fa-solid fa-caret-right"></i>
                  </button>
                </div>
                <span className="label">
                  {t(
                    ACCESSIBILITY_PREVIEW.SPEED_LABEL.key,
                    ACCESSIBILITY_PREVIEW.SPEED_LABEL.value,
                  )}
                </span>
              </div>
              {voices.length > 0 && (
                <div className={`voice-list ${voiceListVisible ? 'voice-list-visible' : ''}`}>
                  <div onClick={() => setVoiceListVisible(!voiceListVisible)}>
                    <span className="label">
                      <i className="fa-solid fa-microphone"></i>{' '}
                      {t(
                        ACCESSIBILITY_PREVIEW.CHANGE_VOICE_LABEL.key,
                        ACCESSIBILITY_PREVIEW.CHANGE_VOICE_LABEL.value,
                      )}
                    </span>
                  </div>
                  <ul className="scroll-container">
                    {voices.map(voice => {
                      return (
                        <li
                          key={voice.name}
                          value={voice.name}
                          onClick={() => {
                            setSelectedVoice(voice);
                            if (isPlaying)
                              readFromIndex(currentWordIndex, voiceSpeed, voice);
                            setVoiceListVisible(false);
                          }}
                          className={selectedVoice.name === voice.name ? 'selected' : ''}
                        >
                          {voice.name}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
          {(!window.speechSynthesis || !voices.length) && (
            <Alert
              description={
                !window.speechSynthesis
                  ? t(
                      ACCESSIBILITY_PREVIEW.VOICE_READING_NOT_SUPPORTED.key,
                      ACCESSIBILITY_PREVIEW.VOICE_READING_NOT_SUPPORTED.value,
                    )
                  : t(
                      ACCESSIBILITY_PREVIEW.VOICE_NOT_AVAILABLE.key,
                      ACCESSIBILITY_PREVIEW.VOICE_NOT_AVAILABLE.value,
                    )
              }
              type="error"
              closable
              showIcon
            />
          )}
        </>
      )}
    </StyledAccessibilityPreview>
  );
};

export default withTranslation(NAMESPACE)(AccessibilityPreview);
